import { Box, Divider, Menu, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import FilterButton from "components/common/FilterButton";
import { isNil } from "lodash";
import React, { useCallback, useState } from "react";

import { useCelleraTheme } from "../../../theme/useCelleraTheme";

interface EFaxFiltersProps {
  dateFrom: string | null;
  dateTo: string | null;
  onChange: (dateFrom: string | null, dateTo: string | null) => void;
}

const toIso = (d: Date | null): string | null =>
  d ? new Date(d).toISOString() : null;

const EFaxFilters: React.FC<EFaxFiltersProps> = ({
  dateFrom,
  dateTo,
  onChange,
}) => {
  const { colors } = useCelleraTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const fromDate = dateFrom ? new Date(dateFrom) : null;
  const toDate = dateTo ? new Date(dateTo) : null;
  const activeCount = (dateFrom ? 1 : 0) + (dateTo ? 1 : 0);

  const handleOpen = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  }, []);

  const handleClose = useCallback(() => setAnchorEl(null), []);

  const handleFromChange = useCallback(
    (d: Date | null) => onChange(toIso(d), dateTo),
    [dateTo, onChange],
  );

  const handleToChange = useCallback(
    (d: Date | null) => onChange(dateFrom, toIso(d)),
    [dateFrom, onChange],
  );

  const handleClearAll = useCallback(() => onChange(null, null), [onChange]);

  return (
    <Box className="flex items-center gap-2">
      <FilterButton activeFiltersCount={activeCount} onClick={handleOpen} />

      <Menu
        anchorEl={anchorEl}
        open={!isNil(anchorEl)}
        onClose={handleClose}
        slotProps={{ paper: { sx: { width: 320, p: 2 } } }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Filters
            </Typography>
            {activeCount > 0 && (
              <Typography
                variant="body2"
                onClick={handleClearAll}
                sx={{
                  color: colors.primary.main,
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Clear All
              </Typography>
            )}
          </Box>

          <Divider />

          <Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                mb: 1,
                color: colors.text.secondary,
              }}
            >
              Date Range
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <DatePicker
                label="From"
                value={fromDate}
                onChange={handleFromChange}
                maxDate={toDate ?? undefined}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
              <DatePicker
                label="To"
                value={toDate}
                onChange={handleToChange}
                minDate={fromDate ?? undefined}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
            </Box>
          </Box>
        </Box>
      </Menu>
    </Box>
  );
};

export default EFaxFilters;
